import { useEffect, useRef, useCallback, useState, createContext, useContext, ReactNode } from 'react';
import Pusher, { Channel, PresenceChannel } from 'pusher-js';

export interface EventHandler<T = any> {
    (data: T): void;
}

export interface ChannelState {
    connected: boolean;
    error: string | null;
    channel: Channel | null;
}

// Context für globale Pusher-Konfiguration
const PusherContext = createContext<Pusher | null>(null);

// Provider-Komponente für Pusher-Konfiguration
export const PusherProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const pusherRef = useRef<Pusher | null>(null);

    if (!pusherRef.current) {
        pusherRef.current = new Pusher('9fe1d961c270a628caaa', {
            cluster: 'eu',
            authEndpoint: import.meta.env.VITE_BROADCAST_AUTH,
            wsHost: import.meta.env.VITE_BROADCAST_HOST,
            wsPort: import.meta.env.VITE_BROADCAST_PORT,
            wsPath: import.meta.env.VITE_BROADCAST_PATH,
            wssPort: import.meta.env.VITE_BROADCAST_PORT,
            forceTLS: import.meta.PROD ? true : false,
            enabledTransports: import.meta.PROD ? ['wss', 'ws'] : ['ws'],
            auth: {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('token')
                }
            }
        });
    }

    useEffect(() => {
        return () => {
            if (pusherRef.current) {
                pusherRef.current.disconnect();
                pusherRef.current = null;
            }
        };
    }, []);

    return (
        <PusherContext.Provider value={ pusherRef.current }>
            { children }
        </PusherContext.Provider>
    );
};

// Hook um Pusher-Instanz zu erhalten
const usePusher = (): Pusher => {
    const pusher = useContext(PusherContext);
    if (!pusher) {
        throw new Error('usePusher muss innerhalb eines PusherProvider verwendet werden');
    }
    return pusher;
};

// Base Hook für Channel-Verbindungen mit Event-Handling
const useChannel = (
    channelName: string,
    events: Record<string, EventHandler> = {},
    isPrivate: boolean = false,
    onConnected?: () => void
): ChannelState => {
    const [state, setState] = useState<ChannelState>({
        connected: false,
        error: null,
        channel: null,
    });

    const pusher = usePusher();
    const channelRef = useRef<Channel | null>(null);
    const eventsRef = useRef(events);
    const onConnectedRef = useRef(onConnected);

    // Refs aktualisieren ohne Re-Subscribe
    useEffect(() => {
        eventsRef.current = events;
        onConnectedRef.current = onConnected;
    }, [events, onConnected]);

    useEffect(() => {
        if (!channelName) return;

        try {
            // Channel abonnieren
            const fullChannelName = isPrivate ? `private-${channelName}` : channelName;
            const channel = pusher.subscribe(fullChannelName);
            channelRef.current = channel;

            // Connection State Events
            const handleConnected = () => {
                setState(prev => ({ ...prev, connected: true, error: null }));
                // onConnected Callback ausführen
                if (onConnectedRef.current) {
                    onConnectedRef.current();
                }
            };

            const handleError = (error: any) => {
                setState(prev => ({ ...prev, error: error.message || 'Connection error' }));
            };

            const handleDisconnected = () => {
                setState(prev => ({ ...prev, connected: false }));
            };

            // Connection Events registrieren
            pusher.connection.bind('connected', handleConnected);
            pusher.connection.bind('error', handleError);
            pusher.connection.bind('disconnected', handleDisconnected);

            // Channel Events binden
            Object.entries(eventsRef.current).forEach(([eventName, handler]) => {
                const wrappedHandler = (data: any) => {
                    eventsRef.current[eventName]?.(data);
                };
                channel.bind(eventName, wrappedHandler);
            });

            // Initial state setzen
            setState({
                connected: pusher.connection.state === 'connected',
                error: null,
                channel,
            });

            // Falls bereits verbunden, onConnected ausführen
            if (pusher.connection.state === 'connected' && onConnectedRef.current) {
                onConnectedRef.current();
            }

            // Cleanup function
            return () => {
                if (channelRef.current) {
                    pusher.unsubscribe(fullChannelName);
                }

                pusher.connection.unbind('connected', handleConnected);
                pusher.connection.unbind('error', handleError);
                pusher.connection.unbind('disconnected', handleDisconnected);
            };
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : 'Unknown error'
            }));
        }
    }, [channelName, pusher, isPrivate]);

    return state;
};

// Hook für öffentliche Channels mit Events
export const usePublicChannel = (
    channelName: string,
    events: Record<string, EventHandler> = {},
    onConnected?: () => void
): ChannelState => {
    return useChannel(channelName, events, false, onConnected);
};

// Hook für private Channels mit Events
export const usePrivateChannel = (
    channelName: string,
    events: Record<string, EventHandler> = {},
    onConnected?: () => void
): ChannelState => {
    return useChannel(channelName, events, true, onConnected);
};

// Hook für einzelne Event-Handler (wenn man nur ein Event braucht)
export const useChannelEvent = <T = any>(
    channelName: string,
    eventName: string,
    handler: EventHandler<T>,
    isPrivate: boolean = false,
    onConnected?: () => void
) => {
    return useChannel(
        channelName,
        { [eventName]: handler },
        isPrivate,
        onConnected
    );
};

// Presence Channel Hook
export const usePresenceChannel = (
    channelName: string,
    events: Record<string, EventHandler> = {},
    onConnected?: () => void
) => {
    const [members, setMembers] = useState<Record<string, any>>({});
    const channelState = useChannel(`presence-${channelName}`, events, true, onConnected);

    useEffect(() => {
        const presenceChannel = channelState.channel as PresenceChannel;
        if (!presenceChannel) return;

        const handleSubscriptionSucceeded = (members: any) => {
            setMembers(members.members);
        };

        const handleMemberAdded = (member: any) => {
            setMembers(prev => ({ ...prev, [member.id]: member.info }));
        };

        const handleMemberRemoved = (member: any) => {
            setMembers(prev => {
                const updated = { ...prev };
                delete updated[member.id];
                return updated;
            });
        };

        presenceChannel.bind('pusher:subscription_succeeded', handleSubscriptionSucceeded);
        presenceChannel.bind('pusher:member_added', handleMemberAdded);
        presenceChannel.bind('pusher:member_removed', handleMemberRemoved);

        return () => {
            presenceChannel.unbind('pusher:subscription_succeeded', handleSubscriptionSucceeded);
            presenceChannel.unbind('pusher:member_added', handleMemberAdded);
            presenceChannel.unbind('pusher:member_removed', handleMemberRemoved);
        };
    }, [channelState.channel]);

    return {
        ...channelState,
        members,
        memberCount: Object.keys(members).length,
    };
};

// Utility Hook zum Senden von Client Events
export const useClientEvents = (channel: Channel | null) => {
    const trigger = useCallback((eventName: string, data: any) => {
        if (channel && 'trigger' in channel) {
            (channel as any).trigger(`client-${eventName}`, data);
        }
    }, [channel]);

    return { trigger };
};